import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { HeatmapData } from '../../types/analytics';

interface RegionHeatmapProps {
  data: HeatmapData[];
  title?: string;
  height?: number;
  showLabels?: boolean;
  onRegionClick?: (data: HeatmapData) => void;
}

const RegionHeatmap: React.FC<RegionHeatmapProps> = ({
  data,
  title = 'Attendance by Region',
  height = 400,
  showLabels = true,
  onRegionClick,
}) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Sort by value for better visualization
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    
    // Create treemap data
    // Size tiles by attendance percentage (item.value) as requested
    const treemapData = sortedData.map(item => ({
      name: item.stateName,
      // Use attendance percentage for sizing; ensure a minimum value so tiles remain visible
      value: Math.max(1, Number(item.value) || 0),
      itemStyle: {
        color: getHeatColor(Number(item.value) || 0),
      },
      data: item,
    }));

    return {
      title: title ? {
        text: title,
        left: 'center',
        top: 10,
        textStyle: { fontSize: 16, fontWeight: 600, color: '#374151' }
      } : undefined,
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#374151' },
        formatter: (params: any) => {
          const d = params?.data?.data as HeatmapData | undefined;
          if (!d) return '';
          const value = Number(d.value) ?? 0;
          return `
            <div style="min-width: 140px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${d.stateName}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Attendance:</span>
                <span style="font-weight: 700; color: ${getHeatColor(value)};">${value.toFixed(1)}%</span>
              </div>
            </div>
          `;
        }
      },
      series: [{
        type: 'treemap',
        top: title ? 50 : 20,
        left: 10,
        right: 10,
        bottom: 40,
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        data: treemapData,
        label: {
          show: showLabels,
          formatter: (params: any) => {
            const d = params?.data?.data as HeatmapData | undefined;
            if (!d) return '';
            const value = d.value ?? 0;
            return `{name|${d.stateName}}\n{value|${value.toFixed(0)}%}`;
          },
          rich: {
            name: {
              fontSize: 11,
              fontWeight: 500,
              color: '#fff',
              textShadowColor: 'rgba(0,0,0,0.3)',
              textShadowBlur: 2,
            },
            value: {
              fontSize: 14,
              fontWeight: 700,
              color: '#fff',
              textShadowColor: 'rgba(0,0,0,0.3)',
              textShadowBlur: 2,
            },
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          gapWidth: 2,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
        levels: [{
          itemStyle: {
            borderRadius: 6,
          },
        }],
      }],
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };
  }, [data, title, showLabels]);

  const handleChartClick = (params: any) => {
    if (params.data?.data && onRegionClick) {
      onRegionClick(params.data.data as HeatmapData);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <p className="text-gray-500">No heatmap data available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <ReactECharts 
        option={option} 
        style={{ height, width: '100%' }}
        opts={{ renderer: 'svg' }}
        onEvents={{ click: handleChartClick }}
      />
      
      {/* Color Legend - outside the chart */}
      <div className="flex items-center justify-center gap-2 text-xs mt-2 pb-2">
        <span className="text-gray-500 font-medium">Low</span>
        <div className="flex h-3 shadow-sm border border-gray-200 rounded">
          <div className="w-6" style={{ background: '#ef4444' }} />
          <div className="w-6" style={{ background: '#f97316' }} />
          <div className="w-6" style={{ background: '#f59e0b' }} />
          <div className="w-6" style={{ background: '#84cc16' }} />
          <div className="w-6" style={{ background: '#10b981' }} />
        </div>
        <span className="text-gray-500 font-medium">High</span>
      </div>
    </div>
  );
};

// Helper function to get heat color based on attendance percentage
const getHeatColor = (value: number): string => {
  if (value >= 95) return '#10b981'; // emerald-500
  if (value >= 85) return '#22c55e'; // green-500
  if (value >= 75) return '#84cc16'; // lime-500
  if (value >= 65) return '#f59e0b'; // amber-500
  if (value >= 50) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
};

export default RegionHeatmap;
